<template>
  <div class="cards-viewer animate__animated animate__fadeIn">

    <!-- ── SUCCESS STATE ── shown after voting -->
    <transition name="pop">
      <div v-if="selectedCard" class="cards-success-overlay" :style="{ '--brand': brandColor }">
        <div class="cso-card-wrapper">
          <div class="cso-card-preview" :style="selectedCard.type === 'text' ? { background: selectedCard.color || brandColor } : {}">
            <template v-if="selectedCard.type === 'image' && selectedCard.imageUrl">
              <img :src="selectedCard.imageUrl" class="cso-img" />
              <div class="cso-img-gradient"></div>
            </template>
            <div v-else class="cso-text-content">
              <p class="cso-card-title">{{ selectedCard.title }}</p>
            </div>
          </div>
          <!-- Big checkmark badge — outside cso-card-preview to avoid overflow:hidden clipping -->
          <div class="cso-check-badge" :style="{ background: brandColor }">
            <svg viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
        </div>

        <div class="cso-info">
          <h2 class="cso-title">¡Elección registrada!</h2>
          <p class="cso-subtitle">Tu voto por <strong>{{ selectedCard.title || 'esta opción' }}</strong> ha sido guardado</p>
          <div class="cso-confetti-row">
            <span>🎉</span><span>✨</span><span>🎊</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── CARD PICKER ── shown before voting -->
    <template v-if="!selectedCard">
      <div class="cards-user-header">
        <h2 class="cards-heading">{{ titulo || 'Elige una opción' }}</h2>
        <p class="cards-subheading">Desliza y selecciona tu favorita</p>
      </div>

      <swiper
        :initialSlide="initialIndex"
        :spaceBetween="10"
        :slidesPerView="1"
        :modules="modules"
        effect="cards"
        :cardsEffect="{
          perSlideOffset: 8,
          perSlideRotate: 2,
          rotate: true,
          slideShadows: false
        }"
        :style="{ '--brand': brandColor }"
        @swiper="onSwiper"
        @slideChange="onSlideChange"
        class="moment-swiper"
      >
        <swiper-slide
          v-for="(card, index) in moments"
          :key="index"
          class="moment-slide"
        >
          <div
            class="moment-content-wrapper"
            :style="{ background: card.type === 'text' ? card.color : '#1a1a1a' }"
          >
            <template v-if="card.type === 'image' && card.imageUrl">
              <img :src="card.imageUrl" class="moment-image" loading="eager" />
              <div class="image-overlay-gradient"></div>
            </template>

            <div v-else class="text-card-content">
              <h2 class="card-display-text">{{ card.title }}</h2>
            </div>

            <div class="card-index-badge">#{{ index + 1 }}</div>

            <!-- Active selection ring — only on the frontmost card -->
            <div class="card-active-ring"></div>
          </div>
        </swiper-slide>
      </swiper>

      <div class="controls-overlay">
        <button
          class="btn-choose-response"
          :style="{ background: brandColor }"
          @click="confirmarEleccion"
          :disabled="votoBloqueado"
        >
          <span>ELEGIR ESTA OPCIÓN</span>
          <svg class="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { EffectCards } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/effect-cards'

export default {
  name: 'CardsViewer',
  components: { Swiper, SwiperSlide },

  props: {
    moments:        { type: Array,   required: true },
    initialIndex:   { type: Number,  default: 0 },
    brandColor:     { type: String,  default: '#F46A76' },
    votoBloqueado:  { type: Boolean, default: false },
    titulo:         { type: String },
    preselectedCard:{ type: Object,  default: null }
  },

  emits: ['vote', 'change'],

  data() {
    return {
      swiper:       null,
      modules:      [EffectCards],
      activeIdx:    this.initialIndex,
      selectedCard: this.preselectedCard || null
    }
  },

  watch: {
    // If parent resets votoBloqueado (new session), clear local success state
    votoBloqueado(val) {
      if (!val) this.selectedCard = null;
    },
    // Restore success state when preselectedCard arrives (e.g. after async restore)
    preselectedCard(val) {
      if (val && !this.selectedCard) this.selectedCard = val;
      if (!val) this.selectedCard = null;
    }
  },

  methods: {
    onSwiper(swiper) {
      this.swiper = swiper;
    },

    confirmarEleccion() {
      if (this.votoBloqueado) return;
      const cardActual = this.moments[this.activeIdx];
      this.selectedCard = cardActual;       // triggers success overlay
      this.$emit('vote', cardActual);
    },

    onSlideChange(swiper) {
      this.activeIdx = swiper.activeIndex;
      this.$emit('change', swiper.activeIndex);
    }
  }
}
</script>

<style scoped>
/* ── Viewer shell ─────────────────────────────────────────────────────── */
.cards-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.cards-heading {
  font-weight: 800;
  font-size: 20px;
  text-align: center;
  color: white;
  margin: 0 0 4px;
}
.cards-subheading {
  text-align: center;
  opacity: 0.55;
  font-size: 13px;
  color: white;
  margin: 0 0 16px;
}

/* ── Swiper ───────────────────────────────────────────────────────────── */
.moment-swiper {
  width: 85%;
  max-width: 320px;
  height: 46vh;
  margin: 0 auto;
}

.moment-slide {
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0,0,0,0.6);
}

.moment-content-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Active-card selection ring — hidden by default, shown on .swiper-slide-active */
.card-active-ring {
  position: absolute;
  inset: 0;
  border-radius: 28px;
  pointer-events: none;
  border: 3px solid transparent;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.swiper-slide-active .card-active-ring {
  border-color: var(--brand, #F46A76);
  box-shadow: 0 0 0 5px rgba(255,255,255,0.12),
              0 0 30px rgba(255,255,255,0.08);
}

.moment-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, rgba(0,0,0,0.5) 0%, transparent 55%);
}

.text-card-content {
  padding: 28px;
  text-align: center;
}

.card-display-text {
  font-weight: 800;
  font-size: 26px;
  color: white;
  line-height: 1.3;
}

.card-index-badge {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0,0,0,0.35);
  backdrop-filter: blur(6px);
  color: white;
  padding: 4px 11px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 800;
}

/* ── Choose button ────────────────────────────────────────────────────── */
.controls-overlay {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 0 8px;
}

.btn-choose-response {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 80%;
  padding: 18px 24px;
  border-radius: 50px;
  border: none;
  color: white;
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275),
              box-shadow 0.2s ease;
  box-shadow: 0 8px 28px rgba(0,0,0,0.4);
}

.btn-choose-response:active:not(:disabled) {
  transform: scale(0.95);
}

.btn-choose-response:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-arrow {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* ── Success overlay ──────────────────────────────────────────────────── */
.cards-success-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 24px 20px;
  width: 100%;
  max-width: 340px;
}

.cso-card-wrapper {
  position: relative;
  display: inline-block;
  animation: cardPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
}

.cso-card-preview {
  position: relative;
  width: 220px;
  height: 280px;
  border-radius: 28px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6),
              0 0 0 3px var(--brand, #F46A76),
              0 0 0 8px rgba(255,255,255,0.12);
}

@keyframes cardPop {
  0%   { transform: scale(0.7) translateY(30px); opacity: 0; }
  100% { transform: scale(1) translateY(0);       opacity: 1; }
}

.cso-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cso-img-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, rgba(0,0,0,0.5) 0%, transparent 55%);
}
.cso-text-content {
  padding: 24px;
  text-align: center;
}
.cso-card-title {
  font-weight: 800;
  font-size: 22px;
  color: white;
  line-height: 1.3;
  margin: 0;
}

/* Floating checkmark badge — relative to wrapper, not clipped */
.cso-check-badge {
  position: absolute;
  bottom: -16px;
  right: -16px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 3px solid rgba(255,255,255,0.9);
  box-shadow: 0 6px 20px rgba(0,0,0,0.4);
  animation: checkPop 0.4s 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
}

@keyframes checkPop {
  0%   { transform: scale(0); }
  100% { transform: scale(1); }
}

.cso-check-badge svg {
  width: 22px;
  height: 22px;
}

/* Info text */
.cso-info {
  text-align: center;
  animation: fadeUp 0.4s 0.2s ease both;
}

@keyframes fadeUp {
  0%   { transform: translateY(16px); opacity: 0; }
  100% { transform: translateY(0);    opacity: 1; }
}

.cso-title {
  font-weight: 800;
  font-size: 22px;
  color: white;
  margin: 0 0 6px;
}

.cso-subtitle {
  font-size: 14px;
  color: rgba(255,255,255,0.65);
  margin: 0 0 14px;
  line-height: 1.5;
}

.cso-confetti-row {
  display: flex;
  gap: 12px;
  justify-content: center;
  font-size: 22px;
}

/* ── Transition ────────────────────────────────────────────────────────── */
.pop-enter-active { transition: all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
.pop-enter-from   { opacity: 0; transform: scale(0.9); }
</style>
